import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRightCircle,
  ArrowLeftCircle,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Calendar,
  X,
  Send,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/app/components/ui/collapsible';
import { toast } from '@/app/components/ui/simple-toaster';
import { DeploymentCard } from '@/components/deployments/deployment-card';
import {
  useDeployments,
  useApproveDeployment,
  useRejectDeployment,
  useLocations,
  useOrganizationUsers,
  type DeploymentFilters,
} from '@/hooks/use-deployments';
import { useProject } from '@/hooks/use-projects';
import { usePermissions } from '@/hooks/use-permissions';
import type { DeploymentAction, ApprovalStatus } from '@/lib/types';

const PAGE_SIZE = 20;

export function DeploymentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectParam = searchParams.get('project');
  const projectFilter = projectParam && /^\d+$/.test(projectParam)
    ? parseInt(projectParam, 10)
    : '';
  const activeFilter = searchParams.get('active') === 'true' || '';
  const [filters, setFilters] = useState<DeploymentFilters>({
    page: 1,
    page_size: PAGE_SIZE,
    ordering: '-timestamp',
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);

  const { data, isLoading, isError } = useDeployments({
    ...filters,
    project: projectFilter,
    active: activeFilter,
  });
  const { data: locations } = useLocations();
  const { data: users } = useOrganizationUsers();
  const { data: selectedProject } = useProject(
    typeof projectFilter === 'number' ? projectFilter : 0
  );
  const approveMutation = useApproveDeployment();
  const rejectMutation = useRejectDeployment();
  const { canManageGear } = usePermissions();

  const deployments = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const currentPage = filters.page || 1;

  const overdueCount = deployments.filter((d) => d.overdue).length;

  const handleFilterChange = (key: keyof DeploymentFilters, value: string | number | boolean) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      page_size: PAGE_SIZE,
      ordering: '-timestamp',
    });
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('project');
    newParams.delete('active');
    setSearchParams(newParams);
  };

  const clearProjectFilter = () => {
    setFilters((previous) => ({ ...previous, page: 1 }));
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('project');
    newParams.delete('active');
    setSearchParams(newParams);
  };

  const handleApprove = async (id: number) => {
    setApprovingId(id);
    try {
      await approveMutation.mutateAsync(id);
      toast.success('Deployment approved successfully');
    } catch (_error) {
      toast.error('Failed to approve deployment');
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (id: number) => {
    setRejectingId(id);
    try {
      await rejectMutation.mutateAsync(id);
      toast.success('Deployment rejected');
    } catch (_error) {
      toast.error('Failed to reject deployment');
    } finally {
      setRejectingId(null);
    }
  };

  const goToPage = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const hasActiveFilters =
    filters.action ||
    filters.overdue ||
    filters.assigned_to_user ||
    filters.location ||
    filters.date_after ||
    filters.date_before ||
    filters.approval_status ||
    projectFilter ||
    activeFilter;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Deployments</h1>
          <p className="text-gray-600 mt-1">Track all equipment check-outs and check-ins</p>
        </div>
        <div className="flex gap-2">
          {canManageGear && (
            <>
              <Button asChild variant="outline">
                <Link to="/deployments/check-in">
                  <ArrowLeftCircle className="h-4 w-4 mr-2" />
                  Check In
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/deployments/check-out">
                  <ArrowRightCircle className="h-4 w-4 mr-2" />
                  Check Out
                </Link>
              </Button>
              <Button asChild>
                <Link to="/deployments/send-gear">
                  <Send className="h-4 w-4 mr-2" />
                  Send gear
                </Link>
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
            <div className="text-sm text-gray-600">Total Deployments</div>
          </CardContent>
        </Card>
        <Card className={overdueCount > 0 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="p-4">
            <div className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {overdueCount}
            </div>
            <div className="text-sm text-gray-600 flex items-center gap-1">
              {overdueCount > 0 && <AlertTriangle className="h-4 w-4 text-red-500" />}
              Overdue (on this page)
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900">
              {deployments.filter((d) => d.approval_status === 'pending').length}
            </div>
            <div className="text-sm text-gray-600">Pending Approval</div>
          </CardContent>
        </Card>
      </div>

      <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </Button>
          </CollapsibleTrigger>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>

        <CollapsibleContent className="mt-4">
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Action Type</Label>
                  <Select
                    value={filters.action || 'all'}
                    onValueChange={(value) => handleFilterChange('action', value === 'all' ? '' : value as DeploymentAction | '')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All actions</SelectItem>
                      <SelectItem value="check_out">Check Out</SelectItem>
                      <SelectItem value="check_in">Check In</SelectItem>
                      <SelectItem value="transfer">Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Approval Status</Label>
                  <Select
                    value={filters.approval_status || 'all'}
                    onValueChange={(value) => handleFilterChange('approval_status', value === 'all' ? '' : value as ApprovalStatus | '')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Overdue Only</Label>
                  <Select
                    value={filters.overdue === true ? 'true' : filters.overdue === false ? 'false' : 'all'}
                    onValueChange={(value) =>
                      handleFilterChange('overdue', value === 'true' ? true : value === 'false' ? false : '')
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="true">Overdue Only</SelectItem>
                      <SelectItem value="false">Not Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Assigned User</Label>
                  <Select
                    value={filters.assigned_to_user?.toString() || 'all'}
                    onValueChange={(value) => handleFilterChange('assigned_to_user', value !== 'all' ? parseInt(value, 10) : '')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All users" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All users</SelectItem>
                      {users?.map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select
                    value={filters.location?.toString() || 'all'}
                    onValueChange={(value) => handleFilterChange('location', value !== 'all' ? parseInt(value, 10) : '')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All locations</SelectItem>
                      {locations?.map((location) => (
                        <SelectItem key={location.id} value={location.id.toString()}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date From</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="date"
                      className="pl-10"
                      value={filters.date_after || ''}
                      onChange={(e) => handleFilterChange('date_after', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Date To</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="date"
                      className="pl-10"
                      value={filters.date_before || ''}
                      onChange={(e) => handleFilterChange('date_before', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {projectFilter && (
        <Badge variant="secondary" className="w-fit gap-1 py-1 pl-3 pr-1">
          Project: {selectedProject?.name || `#${projectFilter}`}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full"
            onClick={clearProjectFilter}
            aria-label="Remove project filter"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </Badge>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-4 w-64" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Deployments</h2>
            <p className="text-gray-600">
              There was a problem loading the deployments. Please try again.
            </p>
          </CardContent>
        </Card>
      ) : deployments.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ArrowRightCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">
              {hasActiveFilters ? 'No deployments match your filters' : 'No deployments yet'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {hasActiveFilters
                ? 'Try adjusting your filter criteria'
                : 'Check out an asset to start tracking equipment deployments'}
            </p>
            {hasActiveFilters ? (
              <Button variant="link" onClick={clearFilters} className="mt-2">
                Clear filters
              </Button>
            ) : (
              <Button asChild className="mt-4">
                <Link to="/deployments/check-out">
                  <ArrowRightCircle className="h-4 w-4 mr-2" />
                  Check Out Asset
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {deployments.map((deployment) => (
              <DeploymentCard
                key={deployment.id}
                deployment={deployment}
                showActions={canManageGear}
                onApprove={handleApprove}
                onReject={handleReject}
                isApproving={approvingId === deployment.id}
                isRejecting={rejectingId === deployment.id}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} deployments
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
